import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { desktopBridge } from '../services/desktopBridge';
import { DesktopAppRequiredDialog } from '../common/components/_misc/DesktopAppRequiredDialog';

interface BridgeRequiredContextValue {
  /**
   * Gate an action that requires the desktop bridge.
   *
   * Behaviour:
   *  - If the bridge is already known-connected → returns true immediately.
   *  - If the state is unknown OR the retry cooldown has elapsed → makes one
   *    best-effort connection attempt.
   *  - If the bridge is unavailable (or the attempt fails) → logs
   *    `BRIDGE_ACTION: <action>`, opens the "Desktop App Required" dialog,
   *    and returns false.  No further automatic probes are made until the
   *    cooldown elapses or the user explicitly clicks "Launch Desktop App".
   */
  requireBridge: (action: string) => Promise<boolean>;
}

const BridgeRequiredContext = createContext<BridgeRequiredContextValue>({
  requireBridge: async () => false,
});

export function useBridgeRequired(): BridgeRequiredContextValue {
  return useContext(BridgeRequiredContext);
}

export const BridgeRequiredProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pendingAction, setPendingAction] = useState('');

  const requireBridge = useCallback(
    async (action: string): Promise<boolean> => {
      // Fast-path: bridge already confirmed connected this session.
      if (desktopBridge.getConnectionState() === 'connected') {
        return true;
      }

      // Retry eligible (unknown state, or cooldown has elapsed): make one
      // best-effort probe before deciding the bridge is unavailable.
      if (desktopBridge.canRetry()) {
        const connected = await desktopBridge.tryConnect();
        if (connected) return true;
      }

      // Bridge unavailable — record the attempted action and prompt the user.
      console.log(`BRIDGE_ACTION: ${action}`);
      setPendingAction(action);
      setDialogOpen(true);
      return false;
    },
    []
  );

  // Expose requireBridge on window for Cypress E2E tests (dev / test builds only).
  useEffect(() => {
    if (import.meta.env.DEV) {
      (window as unknown as Record<string, unknown>).__testBridgeRequired =
        requireBridge;
    }
    return () => {
      if (import.meta.env.DEV) {
        delete (window as unknown as Record<string, unknown>)
          .__testBridgeRequired;
      }
    };
  }, [requireBridge]);

  return (
    <BridgeRequiredContext.Provider value={{ requireBridge }}>
      {children}
      <DesktopAppRequiredDialog
        open={dialogOpen}
        action={pendingAction}
        onClose={() => setDialogOpen(false)}
      />
    </BridgeRequiredContext.Provider>
  );
};
